import { Controller } from "@hotwired/stimulus"

/*
 * VisionFormController — manages dynamic "Reference Photos" sections
 * in both the new and edit vision forms.
 *
 * Features:
 *  - Adding new photo field blocks via "＋ Add Photo" button
 *  - Live image preview when a file is selected
 *  - Initial index can be set from the server (new form) or counted client-side (edit form)
 */
export default class extends Controller {
  static targets = [ "container", "addButton" ]
  static values = { initialIndex: Number, confirm: String }

  connect() {
    // Determine starting index
    if (this.hasInitialIndexValue) {
      this._photoIndex = this.initialIndexValue
    } else {
      this._photoIndex = this.containerTarget
        .querySelectorAll('[data-photo-wrapper]').length
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Actions                                                           */
  /* ------------------------------------------------------------------ */

  /** Called when "＋ Add Photo" button is clicked */
  addPhoto(event) {
    event.preventDefault()
    this._insertPhotoBlock(this._photoIndex)
    this._photoIndex++
    this._bindPreviews()
  }

  /**
   * Persisted photographs are marked for destruction and submitted with the theme.
   * Photographs that have not been saved yet are dropped from the form.
   */
  removePhoto(event) {
    event.preventDefault()
    if (this.hasConfirmValue && this.confirmValue && !window.confirm(this.confirmValue)) return

    const wrapper = event.target.closest("[data-photo-wrapper]")
    if (!wrapper) return

    const idInput = wrapper.querySelector('input[name$="[id]"]')
    const destroyInput = wrapper.querySelector('input[name$="[_destroy]"]')

    if (idInput && idInput.value && destroyInput) {
      destroyInput.value = "1"
      wrapper.classList.add("hidden")
      return
    }

    wrapper.remove()
  }

  /** Called on `change` of any file input inside the container */
  preview(event) {
    const input = event.target
    const file = input.files && input.files[0]
    if (!file) return

    const wrapper = input.closest('[data-photo-wrapper]')
    if (!wrapper) return

    const previewContainer = wrapper.querySelector(this.data.get("preview-selector") || ".preview-container")
    const previewImg = wrapper.querySelector(this.data.get("img-selector") || ".preview-img")

    if (!previewContainer || !previewImg) return

    const reader = new FileReader()
    reader.onload = (e) => {
      previewImg.src = e.target.result
      previewContainer.classList.remove("hidden")
    }
    reader.readAsDataURL(file)
  }

  /* ------------------------------------------------------------------ */
  /*  Private helpers                                                   */
  /* ------------------------------------------------------------------ */

  _insertPhotoBlock(index) {
    const namePrefix = `vision[vision_photos_attributes][${index}]`

    const templateEl = this.element.querySelector(
      'template[data-vision-form-target="blockTemplate"]'
    )

    if (templateEl) {
      // Prefer server-rendered template
      const clone = templateEl.content.cloneNode(true)
      this._replaceNames(clone, index)
      this.containerTarget.appendChild(clone)
    } else {
      // Fallback when the page has no server-rendered template
      const wrapper = document.createElement("div")
      wrapper.setAttribute("data-photo-wrapper", "true")
      wrapper.className = "vision-photo-fields mb-4 p-4 border border-gray-200 rounded bg-white"
      wrapper.innerHTML = this._photoHTML(namePrefix)
      this.containerTarget.appendChild(wrapper)
    }
  }

  /** Replace placeholder `__INDEX__` with the nested-attributes index */
  _replaceNames(node, index) {
    node.querySelectorAll('[name*="__INDEX__"]').forEach(el => {
      el.name = el.name.replace("__INDEX__", String(index))
    })
  }

  _photoHTML(namePrefix) {
    return `
      <div class="mb-3">
        <label class="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Title</label>
        <input type="text" name="${namePrefix}[title]" class="w-full p-2 border border-gray-300 rounded text-sm" placeholder="Photograph title" />
      </div>
      <div class="mb-3">
        <label class="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Description</label>
        <textarea name="${namePrefix}[description]" rows="3" class="w-full p-2 border border-gray-300 rounded text-sm" placeholder="Describe this photograph."></textarea>
      </div>
      <div class="mb-3">
        <input type="file" name="${namePrefix}[image]" accept="image/*"
          class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-6 file:border-0 file:text-sm file:font-bold file:bg-black file:text-white hover:file:bg-gray-800 file:cursor-pointer photo-file-input"
          data-action="change->vision-form#preview" />
        <div class="preview-container mt-2 hidden">
          <img class="max-h-48 rounded border border-gray-200 preview-img" />
        </div>
      </div>
      <input type="hidden" name="${namePrefix}[_destroy]" value="false" />
      <div class="flex justify-end">
        <button type="button" data-action="click->vision-form#removePhoto" class="text-red-500 text-xs font-bold uppercase tracking-widest hover:text-red-700 cursor-pointer">Remove</button>
      </div>
    `
  }

  /** Attach the preview listener to every file input that doesn't already have it */
  _bindPreviews() {
    this.containerTarget
      .querySelectorAll('input[type="file"][name*="[image]"]')
      .forEach(input => {
        if (!input.dataset.previewBound) {
          input.addEventListener("change", this.preview.bind(this))
          input.dataset.previewBound = "true"
        }
      })
  }
}
